import { useState, useRef, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'

export default function Newsletter() {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef<TurnstileInstance>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!turnstileToken) {
      setStatus('error')
      return
    }
    setStatus('sending')
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          lang: i18n.language,
          'cf-turnstile-response': turnstileToken,
        }),
      })
      setStatus(res.ok ? 'success' : 'error')
    } catch {
      setStatus('error')
    } finally {
      turnstileRef.current?.reset()
    }
  }

  return (
    <section id="newsletter" className="bg-sand py-24 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl mx-auto text-center"
      >
        <p className="font-body text-rot text-xs tracking-widest uppercase mb-3">
          {t('newsletter.kicker')}
        </p>
        <h2 className="font-headline text-espresso text-4xl md:text-5xl mb-4">
          {t('newsletter.h2')}
        </h2>
        <p className="font-body text-espresso/60 text-base leading-relaxed mb-10">
          {t('newsletter.subtext1')}
          <br className="hidden md:block" /> {t('newsletter.subtext2')}
        </p>

        {status === 'success' ? (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-body text-rot text-base"
          >
            {t('newsletter.success')}
          </motion.p>
        ) : (
          <>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter.placeholder')}
                className="flex-1 max-w-sm bg-white border border-espresso/20 rounded-full px-6 py-3 font-body text-espresso placeholder:text-espresso/40 focus:outline-none focus:border-rot transition-colors"
              />
              <button
                type="submit"
                disabled={status === 'sending'}
                className="bg-rot text-sand rounded-full px-7 py-3 font-body hover:bg-lachs transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {status === 'sending' ? t('newsletter.sending') : t('newsletter.button')}
              </button>
            </form>
            <div className="mt-6 flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
                options={{ theme: 'light', size: 'normal' }}
              />
            </div>
          </>
        )}

        {status === 'error' && (
          <p className="font-body text-rot/80 text-sm mt-4">{t('newsletter.error')}</p>
        )}

        <p className="font-body text-espresso/40 text-xs mt-6">
          {t('newsletter.privacy')}{' '}
          <Link to="/privacy" className="underline hover:text-rot transition-colors">
            {t('newsletter.privacyLink')}
          </Link>{' '}
          {t('newsletter.privacyEnd')}
        </p>
      </motion.div>
    </section>
  )
}
